/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Nucleus Admin API
 * Headless, multi-tenant commerce backend for jewelry brands.
 *
 * ## Surfaces
 *
 * | Prefix | Caller | Credential |
 * |---|---|---|
 * | `/platform/v1` | Platform operator | Platform token from **Platform: Auth** |
 * | `/admin/v1` | Staff of one organization | Staff token from **Admin: Auth** |
 * | `/storefront/v1` | A storefront | Store API key from **Admin: API keys** |
 * | `/public/v1` | Anyone (the brand application form) | None; rate limited per IP |
 *
 * Every credential is sent as `Authorization: Bearer <value>`. Use the **Authorize** button once and it applies to every request you try here.
 *
 * ## Conventions
 *
 * - **Ids** are UUID v7 strings. **Timestamps** are ISO 8601 in UTC. **Money** is `{ amount, currency }` with `amount` in integer minor units.
 * - **Lists** paginate by cursor: `?limit=20&cursor=...` in, `{ items, nextCursor }` out, `nextCursor` is `null` on the last page.
 * - **Errors** always use one envelope, `{ error: { code, message, requestId, details?, meta? } }`. Switch on `code`, never on `message`.
 * - **Request ids**: every response carries `x-request-id`. Quote it when reporting a problem.
 * - **Idempotency**: endpoints that create money-related state require an `Idempotency-Key` header; the header is listed on those endpoints.
 * - A record that belongs to another tenant answers **404**, never 403, so existence is not leaked.
 * OpenAPI spec version: 0.1.0
 */
import type {
  AddScheduleMembersRequest,
  BulkResponse,
  BulkScheduleMembersRequest,
  BulkScheduleMembersResponse,
  CreateProductScheduleRequest,
  DeleteAdminV1OrgSchedulesIdMembersProductIdPathParameters,
  DeleteAdminV1OrgSchedulesIdParams,
  DeleteAdminV1OrgSchedulesIdPathParameters,
  DeleteProductScheduleResponse,
  EndProductScheduleRequest,
  GetAdminV1OrgSchedules200,
  GetAdminV1OrgSchedulesIdMembers200,
  GetAdminV1OrgSchedulesIdMembersParams,
  GetAdminV1OrgSchedulesIdMembersPathParameters,
  GetAdminV1OrgSchedulesIdPathParameters,
  GetAdminV1OrgSchedulesIdRuns200,
  GetAdminV1OrgSchedulesIdRunsParams,
  GetAdminV1OrgSchedulesIdRunsPathParameters,
  GetAdminV1OrgSchedulesIdRunsRunIdItems200,
  GetAdminV1OrgSchedulesIdRunsRunIdItemsParams,
  GetAdminV1OrgSchedulesIdRunsRunIdItemsPathParameters,
  GetAdminV1OrgSchedulesIdRunsRunIdPathParameters,
  GetAdminV1OrgSchedulesParams,
  GetAdminV1OrgSchedulesTimelineParams,
  PatchAdminV1OrgSchedulesIdMembersProductIdPathParameters,
  PatchAdminV1OrgSchedulesIdPathParameters,
  PostAdminV1OrgSchedulesIdActivatePathParameters,
  PostAdminV1OrgSchedulesIdEndPathParameters,
  PostAdminV1OrgSchedulesIdMembersBulkPathParameters,
  PostAdminV1OrgSchedulesIdMembersPathParameters,
  PostAdminV1OrgSchedulesIdPausePathParameters,
  PostAdminV1OrgSchedulesIdPreviewPathParameters,
  PostAdminV1OrgSchedulesIdResumePathParameters,
  PostAdminV1OrgSchedulesIdRunNowPathParameters,
  ProductSchedule,
  ProductScheduleChange,
  ProductScheduleCreated,
  RemoveScheduleMemberResponse,
  ScheduleMemberChange,
  SchedulePreview,
  ScheduleRun,
  ScheduleRunNowResponse,
  ScheduleTimeline,
  UpdateProductScheduleRequest,
  UpdateScheduleMemberRequest
} from '../model';

import { customInstance } from '../../../transport/mutator';
import type { BodyType } from '../../../transport/mutator';


type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];


  /**
 * Organisation-level: visible to members who can reach only some of the target stores (D-181 decision 24)
 * @summary Product schedules of the organisation, newest first
 */
export const getAdminV1OrgSchedules = (
    params?: GetAdminV1OrgSchedulesParams,
 options?: SecondParameter<typeof customInstance<GetAdminV1OrgSchedules200>>,) => {
      return customInstance<GetAdminV1OrgSchedules200>(
      {url: `/admin/v1/org/schedules`, method: 'GET',
        params
    },
      options);
    }
  /**
 * At most 100 per organisation (422 LIMIT_EXCEEDED). productIds or an org-scope selection (not both, 422 INVALID_INPUT) join as manual members: each gets a draft store product and a scheduled placement in every store. productIds are all or nothing (422 INVALID_REFERENCE for unknown products, 409 ALREADY_EXISTS for a handle taken in a store, 422 SCHEDULE_CONFLICT for a product another schedule owns there). A selection never fails the create for a product: those products are skipped and the 201 adds memberResult (matched, succeeded, failed, the failures as items). A closed store answers 409 STORE_READ_ONLY. A selection resolves inside the call: 409 SELECTION_CHANGED (meta.expected, meta.actual) when its filter no longer counts expectedCount, 422 SELECTION_TOO_LARGE (meta.limit) past 5,000 products. Needs products.write and access to every target store (404 otherwise).
 * @summary Create a draft rotation
 */
export const postAdminV1OrgSchedules = (
    createProductScheduleRequest: BodyType<CreateProductScheduleRequest>,
 options?: SecondParameter<typeof customInstance<ProductScheduleCreated>>,) => {
      return customInstance<ProductScheduleCreated>(
      {url: `/admin/v1/org/schedules`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: createProductScheduleRequest
    },
      options);
    }
  /**
 * Rotate runs of active schedules predicted from nextRunAt and the cadence (on time, stopping before endsAt and maxCycles; at most 500, then truncated), with the target stores the caller can reach, and store products whose publishAt or unpublishAt falls in the range in those stores (at most 2000). Both halves cover [from, to), ordered by time. to - from is at most 92 days (422). storeId narrows both to one store (404 when the caller cannot reach it); scheduleId keeps that schedule's runs only and no dates (404 when it is not the organisation's)
 * @summary Upcoming rotation runs and one-shot dates
 */
export const getAdminV1OrgSchedulesTimeline = (
    params: GetAdminV1OrgSchedulesTimelineParams,
 options?: SecondParameter<typeof customInstance<ScheduleTimeline>>,) => {
      return customInstance<ScheduleTimeline>(
      {url: `/admin/v1/org/schedules/timeline`, method: 'GET',
        params
    },
      options);
    }
  /**
 * @summary Get a schedule
 */
export const getAdminV1OrgSchedulesId = (
    { id }: GetAdminV1OrgSchedulesIdPathParameters,
 options?: SecondParameter<typeof customInstance<ProductSchedule>>,) => {
      return customInstance<ProductSchedule>(
      {url: `/admin/v1/org/schedules/${id}`, method: 'GET'
    },
      options);
    }
  /**
 * Ended schedules answer 409. stores replaces the targets: an added store gets every member's draft store product and placement (422 SCHEDULE_CONFLICT with meta.scheduleId, meta.storeIds and meta.productIds when another schedule owns one; 409 ALREADY_EXISTS when a member's handle is taken there), a removed store runs a release run (releaseRunId) and keeps its store products. A cadence change on an active schedule moves nextRunAt to the next future run. Needs products.write and access to every target store (404 otherwise).
 * @summary Update a schedule
 */
export const patchAdminV1OrgSchedulesId = (
    { id }: PatchAdminV1OrgSchedulesIdPathParameters,
    updateProductScheduleRequest: BodyType<UpdateProductScheduleRequest>,
 options?: SecondParameter<typeof customInstance<ProductScheduleChange>>,) => {
      return customInstance<ProductScheduleChange>(
      {url: `/admin/v1/org/schedules/${id}`, method: 'PATCH',
      headers: {'Content-Type': 'application/json', },
      data: updateProductScheduleRequest
    },
      options);
    }
  /**
 * Active and paused schedules answer 409; end them first. The store products stay in the stores (204), unless removeDrafts=true: then the drafts the schedule created that were never published and no other schedule holds are removed with it and the answer is 200 with removedDrafts. The schedule's rotation redirects are deleted with it. Needs products.write and access to every target store (404 otherwise).
 * @summary Delete a draft or ended schedule
 */
export const deleteAdminV1OrgSchedulesId = (
    { id }: DeleteAdminV1OrgSchedulesIdPathParameters,
    params?: DeleteAdminV1OrgSchedulesIdParams,
 options?: SecondParameter<typeof customInstance<DeleteProductScheduleResponse | void>>,) => {
      return customInstance<DeleteProductScheduleResponse | void>(
      {url: `/admin/v1/org/schedules/${id}`, method: 'DELETE',
        params
    },
      options);
    }
  /**
 * Needs a target store and a member a run could put live (a priced, active candidate or a pinned member): 422 INVALID_INPUT with meta.reason no_stores or no_candidates otherwise. nextRunAt becomes the first runAt on the cadence not before startsAt; nothing runs at once. Needs products.write and access to every target store (404 otherwise).
 * @summary Activate a draft schedule
 */
export const postAdminV1OrgSchedulesIdActivate = (
    { id }: PostAdminV1OrgSchedulesIdActivatePathParameters,
 options?: SecondParameter<typeof customInstance<ProductSchedule>>,) => {
      return customInstance<ProductSchedule>(
      {url: `/admin/v1/org/schedules/${id}/activate`, method: 'POST'
    },
      options);
    }
  /**
 * A queued run-now or a run in progress stops: it is failed with error "paused" and publishes nothing more. 409 SCHEDULE_NOT_ACTIVE unless active. Needs products.write and access to every target store (404 otherwise).
 * @summary Pause an active schedule
 */
export const postAdminV1OrgSchedulesIdPause = (
    { id }: PostAdminV1OrgSchedulesIdPausePathParameters,
 options?: SecondParameter<typeof customInstance<ProductSchedule>>,) => {
      return customInstance<ProductSchedule>(
      {url: `/admin/v1/org/schedules/${id}/pause`, method: 'POST'
    },
      options);
    }
  /**
 * nextRunAt becomes the next future runAt on the cadence; it never runs at once (use run-now). 409 unless paused. Needs products.write and access to every target store (404 otherwise).
 * @summary Resume a paused schedule
 */
export const postAdminV1OrgSchedulesIdResume = (
    { id }: PostAdminV1OrgSchedulesIdResumePathParameters,
 options?: SecondParameter<typeof customInstance<ProductSchedule>>,) => {
      return customInstance<ProductSchedule>(
      {url: `/admin/v1/org/schedules/${id}/resume`, method: 'POST'
    },
      options);
    }
  /**
 * Applies onEnd (or the body's override) in a release run inside this request: unpublish takes every live product down, pinned ones too; keep leaves them published. Every placement is released and the store products stay. 409 when already ended. Needs products.write and access to every target store (404 otherwise).
 * @summary End a schedule
 */
export const postAdminV1OrgSchedulesIdEnd = (
    { id }: PostAdminV1OrgSchedulesIdEndPathParameters,
    endProductScheduleRequestNull: BodyType<EndProductScheduleRequest | null>,
 options?: SecondParameter<typeof customInstance<ProductScheduleChange>>,) => {
      return customInstance<ProductScheduleChange>(
      {url: `/admin/v1/org/schedules/${id}/end`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: endProductScheduleRequestNull
    },
      options);
    }
  /**
 * Claims the schedule and queues a manual run, which counts as a cycle and leaves nextRunAt as it is. Pausing before it ran cancels it (failed, error "paused"). 409 SCHEDULE_NOT_ACTIVE unless active, 409 CONFLICT while another run holds the claim. Needs products.write and access to every target store (404 otherwise).
 * @summary Run the next rotation now, on the worker
 */
export const postAdminV1OrgSchedulesIdRunNow = (
    { id }: PostAdminV1OrgSchedulesIdRunNowPathParameters,
 options?: SecondParameter<typeof customInstance<ScheduleRunNowResponse>>,) => {
      return customInstance<ScheduleRunNowResponse>(
      {url: `/admin/v1/org/schedules/${id}/run-now`, method: 'POST'
    },
      options);
    }
  /**
 * The rule refresh, the release step and the next batch per store as a run would do them now, in a transaction that is rolled back. firstRunAt says when: nextRunAt, or the first run activating a draft now would give. Ended schedules answer 409
 * @summary Dry run of the next run
 */
export const postAdminV1OrgSchedulesIdPreview = (
    { id }: PostAdminV1OrgSchedulesIdPreviewPathParameters,
 options?: SecondParameter<typeof customInstance<SchedulePreview>>,) => {
      return customInstance<SchedulePreview>(
      {url: `/admin/v1/org/schedules/${id}/preview`, method: 'POST'
    },
      options);
    }
  /**
 * @summary The pool, by position then product id
 */
export const getAdminV1OrgSchedulesIdMembers = (
    { id }: GetAdminV1OrgSchedulesIdMembersPathParameters,
    params?: GetAdminV1OrgSchedulesIdMembersParams,
 options?: SecondParameter<typeof customInstance<GetAdminV1OrgSchedulesIdMembers200>>,) => {
      return customInstance<GetAdminV1OrgSchedulesIdMembers200>(
      {url: `/admin/v1/org/schedules/${id}/members`, method: 'GET',
        params
    },
      options);
    }
  /**
 * Takes productIds or an org-scope selection, exactly one (422 INVALID_INPUT). Per id: 200 added, or already a member (meta.unchanged; with pinned it is pinned as well, and an excluded member rejoins: 422 SCHEDULE_CONFLICT or 409 ALREADY_EXISTS for that id when it cannot), 404 unknown product, 409 ALREADY_EXISTS when its handle is taken in a target store (meta.storeId), 422 SCHEDULE_CONFLICT when another schedule owns it in a target store (skipped; meta.scheduleId, meta.productIds, meta.storeIds of the first owner); a selection answers its failures only and adds matched. 200 when all succeeded, 207 otherwise. 422 SCHEDULE_CONFLICT for the whole call only when another schedule takes one of them at the same moment, 422 LIMIT_EXCEEDED past 5,000 members, 409 when ended. A selection resolves inside the call: 409 SELECTION_CHANGED (meta.expected, meta.actual) when its filter no longer counts expectedCount, 422 SELECTION_TOO_LARGE (meta.limit) past 5,000 products. Needs products.write and access to every target store (404 otherwise).
 * @summary Add products to the pool
 */
export const postAdminV1OrgSchedulesIdMembers = (
    { id }: PostAdminV1OrgSchedulesIdMembersPathParameters,
    addScheduleMembersRequest: BodyType<AddScheduleMembersRequest>,
 options?: SecondParameter<typeof customInstance<BulkResponse>>,) => {
      return customInstance<BulkResponse>(
      {url: `/admin/v1/org/schedules/${id}/members`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: addScheduleMembersRequest
    },
      options);
    }
  /**
 * Each id as PATCH or DELETE of that member would do it, with one release run for everything exclude or remove takes offline (releaseRunId). remove excludes a rule member instead (meta.excluded). 200 when every id succeeded, 207 otherwise; 409 when ended. Needs products.write and access to every target store (404 otherwise).
 * @summary Pin, unpin, exclude, include or remove up to 200 members
 */
export const postAdminV1OrgSchedulesIdMembersBulk = (
    { id }: PostAdminV1OrgSchedulesIdMembersBulkPathParameters,
    bulkScheduleMembersRequest: BodyType<BulkScheduleMembersRequest>,
 options?: SecondParameter<typeof customInstance<BulkScheduleMembersResponse>>,) => {
      return customInstance<BulkScheduleMembersResponse>(
      {url: `/admin/v1/org/schedules/${id}/members/bulk`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: bulkScheduleMembersRequest
    },
      options);
    }
  /**
 * Pinning clears excluded and excluding clears pinned; both true is 422. Excluding releases the member's placements (a release run unpublishes it where it is live: releaseRunId); including it again takes its placements back (422 SCHEDULE_CONFLICT when another schedule took the product meanwhile). Needs products.write and access to every target store (404 otherwise).
 * @summary Pin, exclude or reorder a member
 */
export const patchAdminV1OrgSchedulesIdMembersProductId = (
    { id, productId }: PatchAdminV1OrgSchedulesIdMembersProductIdPathParameters,
    updateScheduleMemberRequest: BodyType<UpdateScheduleMemberRequest>,
 options?: SecondParameter<typeof customInstance<ScheduleMemberChange>>,) => {
      return customInstance<ScheduleMemberChange>(
      {url: `/admin/v1/org/schedules/${id}/members/${productId}`, method: 'PATCH',
      headers: {'Content-Type': 'application/json', },
      data: updateScheduleMemberRequest
    },
      options);
    }
  /**
 * Its placements are released; where it was live a release run unpublishes it (releaseRunId). Its store products stay in the stores. A rule member is excluded instead of removed (excluded: true), because the next rule refresh would add it back; include it again with PATCH. Needs products.write and access to every target store (404 otherwise).
 * @summary Remove a product from the pool
 */
export const deleteAdminV1OrgSchedulesIdMembersProductId = (
    { id, productId }: DeleteAdminV1OrgSchedulesIdMembersProductIdPathParameters,
 options?: SecondParameter<typeof customInstance<RemoveScheduleMemberResponse>>,) => {
      return customInstance<RemoveScheduleMemberResponse>(
      {url: `/admin/v1/org/schedules/${id}/members/${productId}`, method: 'DELETE'
    },
      options);
    }
  /**
 * @summary Runs of a schedule, newest first
 */
export const getAdminV1OrgSchedulesIdRuns = (
    { id }: GetAdminV1OrgSchedulesIdRunsPathParameters,
    params?: GetAdminV1OrgSchedulesIdRunsParams,
 options?: SecondParameter<typeof customInstance<GetAdminV1OrgSchedulesIdRuns200>>,) => {
      return customInstance<GetAdminV1OrgSchedulesIdRuns200>(
      {url: `/admin/v1/org/schedules/${id}/runs`, method: 'GET',
        params
    },
      options);
    }
  /**
 * @summary One run with its counts
 */
export const getAdminV1OrgSchedulesIdRunsRunId = (
    { id, runId }: GetAdminV1OrgSchedulesIdRunsRunIdPathParameters,
 options?: SecondParameter<typeof customInstance<ScheduleRun>>,) => {
      return customInstance<ScheduleRun>(
      {url: `/admin/v1/org/schedules/${id}/runs/${runId}`, method: 'GET'
    },
      options);
    }
  /**
 * @summary What a run did, per store product, in order
 */
export const getAdminV1OrgSchedulesIdRunsRunIdItems = (
    { id, runId }: GetAdminV1OrgSchedulesIdRunsRunIdItemsPathParameters,
    params?: GetAdminV1OrgSchedulesIdRunsRunIdItemsParams,
 options?: SecondParameter<typeof customInstance<GetAdminV1OrgSchedulesIdRunsRunIdItems200>>,) => {
      return customInstance<GetAdminV1OrgSchedulesIdRunsRunIdItems200>(
      {url: `/admin/v1/org/schedules/${id}/runs/${runId}/items`, method: 'GET',
        params
    },
      options);
    }
  export type GetAdminV1OrgSchedulesResult = NonNullable<Awaited<ReturnType<typeof getAdminV1OrgSchedules>>>
export type PostAdminV1OrgSchedulesResult = NonNullable<Awaited<ReturnType<typeof postAdminV1OrgSchedules>>>
export type GetAdminV1OrgSchedulesTimelineResult = NonNullable<Awaited<ReturnType<typeof getAdminV1OrgSchedulesTimeline>>>
export type GetAdminV1OrgSchedulesIdResult = NonNullable<Awaited<ReturnType<typeof getAdminV1OrgSchedulesId>>>
export type PatchAdminV1OrgSchedulesIdResult = NonNullable<Awaited<ReturnType<typeof patchAdminV1OrgSchedulesId>>>
export type DeleteAdminV1OrgSchedulesIdResult = NonNullable<Awaited<ReturnType<typeof deleteAdminV1OrgSchedulesId>>>
export type PostAdminV1OrgSchedulesIdActivateResult = NonNullable<Awaited<ReturnType<typeof postAdminV1OrgSchedulesIdActivate>>>
export type PostAdminV1OrgSchedulesIdPauseResult = NonNullable<Awaited<ReturnType<typeof postAdminV1OrgSchedulesIdPause>>>
export type PostAdminV1OrgSchedulesIdResumeResult = NonNullable<Awaited<ReturnType<typeof postAdminV1OrgSchedulesIdResume>>>
export type PostAdminV1OrgSchedulesIdEndResult = NonNullable<Awaited<ReturnType<typeof postAdminV1OrgSchedulesIdEnd>>>
export type PostAdminV1OrgSchedulesIdRunNowResult = NonNullable<Awaited<ReturnType<typeof postAdminV1OrgSchedulesIdRunNow>>>
export type PostAdminV1OrgSchedulesIdPreviewResult = NonNullable<Awaited<ReturnType<typeof postAdminV1OrgSchedulesIdPreview>>>
export type GetAdminV1OrgSchedulesIdMembersResult = NonNullable<Awaited<ReturnType<typeof getAdminV1OrgSchedulesIdMembers>>>
export type PostAdminV1OrgSchedulesIdMembersResult = NonNullable<Awaited<ReturnType<typeof postAdminV1OrgSchedulesIdMembers>>>
export type PostAdminV1OrgSchedulesIdMembersBulkResult = NonNullable<Awaited<ReturnType<typeof postAdminV1OrgSchedulesIdMembersBulk>>>
export type PatchAdminV1OrgSchedulesIdMembersProductIdResult = NonNullable<Awaited<ReturnType<typeof patchAdminV1OrgSchedulesIdMembersProductId>>>
export type DeleteAdminV1OrgSchedulesIdMembersProductIdResult = NonNullable<Awaited<ReturnType<typeof deleteAdminV1OrgSchedulesIdMembersProductId>>>
export type GetAdminV1OrgSchedulesIdRunsResult = NonNullable<Awaited<ReturnType<typeof getAdminV1OrgSchedulesIdRuns>>>
export type GetAdminV1OrgSchedulesIdRunsRunIdResult = NonNullable<Awaited<ReturnType<typeof getAdminV1OrgSchedulesIdRunsRunId>>>
export type GetAdminV1OrgSchedulesIdRunsRunIdItemsResult = NonNullable<Awaited<ReturnType<typeof getAdminV1OrgSchedulesIdRunsRunIdItems>>>
